import { faBars, faHome, faBuilding, faSignOutAlt, faFileInvoiceDollar, faTasks, faUserShield, faChartLine, faGraduationCap, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import React, { useState, useEffect } from 'react';
import logo from '../assets/images/logo.webp';
import '../assets/Navbar.css';
import { canAccessCashForecast } from '../utils/cashForecastAccess';
import ThemeToggle from './helpers/ThemeToggle';


const Navbar = () => {
    const { logout, user } = useAppContext();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isNavVisible, setIsNavVisible] = useState(!isMobile);
    const navigate = useNavigate();
    const location = useLocation();

    // Determine navigation items based on user type
    const getNavItems = () => {
        // Super Admin: Dashboard, Tasks, Billing, Admin, Tickets
        if (user?.is_super_admin) {
            return [
                { id: 'dashboard', label: 'Dashboard', path: '/', icon: faHome },
                { id: 'tasks', label: 'Tasks', path: '/tasks', icon: faTasks },
                { id: 'billing', label: 'Billing', path: '/billing', icon: faFileInvoiceDollar },
                { id: 'super-admin', label: 'Admin', path: '/super-admin', icon: faUserShield },
                { id: 'tickets', label: 'Tickets', path: '/tickets', icon: faClipboardList },
            ];
        }
        
        // Admin (but not super admin): Dashboard, Billing, Admin (company + users + profile)
        if (user?.is_admin) {
            return [
                { id: 'dashboard', label: 'Dashboard', path: '/', icon: faHome },
                { id: 'billing', label: 'Billing', path: '/billing', icon: faFileInvoiceDollar },
                { id: 'admin', label: 'Admin', path: '/admin', icon: faUserShield },
            ];
        }
        
        // Regular User: Dashboard, Company (company + profile)
        return [
            { id: 'dashboard', label: 'Dashboard', path: '/', icon: faHome },
            { id: 'admin', label: 'Company', path: '/admin', icon: faBuilding },
        ];
    };

    const navItems = (() => {
        const items = [
            ...getNavItems(),
            {
                id: 'tutorials',
                label: 'Tutorials',
                path: '/tutorials',
                icon: faGraduationCap,
            },
        ];

        if (!canAccessCashForecast(user)) return items;

        return [
            ...items,
            {
                id: 'cash-forecast',
                label: 'Cash Forecast',
                path: '/cash-forecast',
                icon: faChartLine,
            },
        ];
    })();

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            setIsNavVisible(!mobile);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLogout = () => {
        logout();
    };

    const toggleNav = () => {
        setIsNavVisible(!isNavVisible);
    };

    const handleNavClick = (path) => {
        navigate(path);
        if (isMobile) {
            setIsNavVisible(false);
        }
    };

    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        if (path === '/billing') {
            return location.pathname === '/billing' || location.pathname === '/invoices';
        }
        return location.pathname === path;
    };

    return (
        <>
            <nav className={`navbar ${!isNavVisible ? 'mobile-hidden' : ''}`}>
                <div className="navbar-top">
                    <img src={logo} alt="Logo" className="navbar-logo" />
                </div>
                <div className="nav-content">
                    <div className="nav-buttons">
                        {navItems.map((item) => (
                            <button 
                                key={item.id}
                                className={`nav-button${isActive(item.path) ? ' active' : ''}`}
                                onClick={() => handleNavClick(item.path)}
                            >
                                <h6 className="nav-label">{item.label}</h6>
                                <FontAwesomeIcon icon={item.icon} className="icon" />
                            </button>
                        ))}
                    </div>
                    <div className="logout-button">
                        {user?.is_super_admin && (
                            <span className="status-chip status-chip--xs status-chip--accent status-chip--block">Admin Mode</span>
                        )}
                        <ThemeToggle variant="nav" />
                        <button
                            type="button"
                            className="theme-toggle"
                            onClick={handleLogout}
                        >
                            <FontAwesomeIcon icon={faSignOutAlt} className="theme-toggle__icon" aria-hidden="true" />
                            Logout
                        </button>
                    </div>
                </div>
            </nav>
            {isMobile && (
                <button 
                    className="mobile-nav-toggle"
                    onClick={toggleNav}
                >
                    <FontAwesomeIcon icon={faBars} />
                </button>
            )}
        </>
    );
};

export default Navbar;
